import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, BACKEND_URL } from "../config/api";
import { Bell, BellOff, Trash2, Plus, AlertTriangle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import {
  registerServiceWorker,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestPush,
  requestNotificationPermission,
  isNotificationSupported,
  getIOSInstallHint,
} from "../utils/notifications";

export default function AlertsManagerPage() {
  const [endpoint, setEndpoint] = useState(null);
  const [tickers, setTickers] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTicker, setNewTicker] = useState("");

  // New custom-alert form
  const [form, setForm] = useState({
    ticker: "",
    alert_type: "price_above",
    threshold: "",
  });

  const apiBase = BACKEND_URL;

  // Pull the current browser subscription's endpoint (if any)
  const loadSubscription = useCallback(async () => {
    setLoading(true);
    try {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setLoading(false);
        return;
      }
      await registerServiceWorker();
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setEndpoint(null);
        setLoading(false);
        return;
      }
      setEndpoint(sub.endpoint);
      const sres = await axios.get(`${API}/push/subscription`, { params: { endpoint: sub.endpoint } });
      if (sres.data?.found) {
        setTickers(sres.data.tickers || []);
      }
      const ares = await axios.get(`${API}/push/custom-alerts`, { params: { endpoint: sub.endpoint } });
      setAlerts(ares.data?.alerts || []);
    } catch (e) {
      console.warn("loadSubscription failed", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSubscription(); }, [loadSubscription]);

  const enableAlerts = async () => {
    const iosHint = getIOSInstallHint();
    if (iosHint) {
      toast.warning("iPhone detected", { description: iosHint, duration: 15000 });
    }
    if (!isNotificationSupported()) {
      toast.error("Push not supported", { description: "Your browser (or iPhone Safari tab) does not support push. On iPhone add to Home Screen first." });
      return;
    }
    const perm = await requestNotificationPermission();
    if (perm !== "granted") {
      toast.error("Permission denied", { description: "Enable notifications in your browser settings." });
      return;
    }
    await registerServiceWorker();
    const sub = await subscribeToPush({ apiBase, tickers: [] });
    if (sub) {
      setEndpoint(sub.endpoint);
      toast.success("Push notifications enabled");
      loadSubscription();
    }
  };

  const disableAlerts = async () => {
    await unsubscribeFromPush({ apiBase });
    setEndpoint(null);
    setTickers([]);
    setAlerts([]);
    toast("Push notifications disabled");
  };

  const fireTest = async () => {
    const ok = await sendTestPush({ apiBase });
    toast[ok ? "success" : "error"](ok ? "Test push sent" : "Test push failed");
  };

  const addTicker = async () => {
    const tk = newTicker.trim().toUpperCase();
    if (!tk || !endpoint) return;
    if (tickers.includes(tk)) { setNewTicker(""); return; }
    const nextTickers = [...tickers, tk];
    try {
      await axios.post(`${API}/push/subscription/tickers`, { endpoint, tickers: nextTickers });
      setTickers(nextTickers);
      setNewTicker("");
      toast.success(`${tk} added to alerts`);
    } catch (e) {
      toast.error("Failed to add ticker");
    }
  };

  const removeTicker = async (tk) => {
    if (!endpoint) return;
    const nextTickers = tickers.filter(t => t !== tk);
    try {
      await axios.post(`${API}/push/subscription/tickers`, { endpoint, tickers: nextTickers });
      setTickers(nextTickers);
      toast(`Removed ${tk}`);
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const createCustomAlert = async (e) => {
    e.preventDefault();
    if (!endpoint) return;
    const tk = form.ticker.trim().toUpperCase();
    const thr = parseFloat(form.threshold);
    if (!tk || !Number.isFinite(thr)) {
      toast.error("Ticker + threshold required");
      return;
    }
    try {
      const res = await axios.post(`${API}/push/custom-alerts`, {
        endpoint,
        ticker: tk,
        alert_type: form.alert_type,
        threshold: thr,
      });
      setAlerts([...alerts, res.data.alert]);
      setForm({ ticker: "", alert_type: "price_above", threshold: "" });
      toast.success("Custom alert created");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create");
    }
  };

  const deleteCustomAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/push/custom-alerts/${alertId}`);
      setAlerts(alerts.filter(a => a.alert_id !== alertId));
      toast("Alert deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const alertTypeLabel = (t) => ({
    price_above: "Price above",
    price_below: "Price below",
    pct_move_above: "% change above",
    pct_move_below: "% change below",
    vix_above: "VIX above",
  })[t] || t;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white p-4 sm:p-6" data-testid="alerts-manager-page">
      <div className="max-w-5xl mx-auto space-y-6">
        <header className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Bell className="w-6 h-6 text-amber-400" />
            <h1 className="text-2xl sm:text-3xl font-bold">Alerts Manager</h1>
          </div>
          <button
            onClick={loadSubscription}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-purple-300 bg-purple-900/40 hover:bg-purple-800/60 rounded-lg border border-purple-800/50"
          >
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
        </header>

        {loading ? (
          <div className="text-gray-400">Loading…</div>
        ) : !endpoint ? (
          <div className="rounded-lg border border-amber-900/40 bg-amber-950/20 p-4 space-y-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <span className="text-amber-300 font-semibold">Push notifications are off for this device</span>
            </div>
            <p className="text-sm text-gray-400">
              Enable to receive native alerts for strength flips, divergences, and custom price/VIX triggers — even when StockPimp is closed.
            </p>
            <button
              onClick={enableAlerts}
              data-testid="enable-push-btn"
              className="inline-flex items-center gap-2 px-4 py-2 mt-2 text-sm font-semibold bg-amber-600 hover:bg-amber-500 rounded-lg text-black"
            >
              <Bell className="w-4 h-4" /> Enable Push Notifications
            </button>
          </div>
        ) : (
          <>
            {/* Subscription controls */}
            <div className="rounded-lg border border-green-900/40 bg-green-950/20 p-4 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-green-400" />
                <span className="text-green-300 font-semibold">Push notifications are ON</span>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={fireTest} className="px-3 py-1.5 text-xs bg-green-800/60 hover:bg-green-700 rounded">
                  Send test push
                </button>
                <button onClick={disableAlerts} className="px-3 py-1.5 text-xs bg-red-900/60 hover:bg-red-800 rounded flex items-center gap-1.5">
                  <BellOff className="w-3.5 h-3.5" /> Turn off
                </button>
              </div>
            </div>

            {/* Ticker watchlist */}
            <section className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
              <h2 className="text-lg font-semibold text-white mb-2">Watched tickers (strength / divergence)</h2>
              <p className="text-xs text-gray-500 mb-3">Get pinged when RSI + MFI flip to EXTREMELY WEAK / STRONG or a divergence forms.</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {tickers.length === 0 && <span className="text-gray-500 text-sm italic">No tickers yet — add one below.</span>}
                {tickers.map((t) => (
                  <span key={t} data-testid={`watched-ticker-${t}`} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-900/50 border border-indigo-700/60 text-indigo-200 text-sm font-mono">
                    {t}
                    <button onClick={() => removeTicker(t)} className="hover:text-red-300" title="Remove">
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
              <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); addTicker(); }}>
                <input
                  value={newTicker}
                  onChange={(e) => setNewTicker(e.target.value.toUpperCase())}
                  placeholder="e.g. AAPL"
                  data-testid="new-ticker-input"
                  className="flex-1 px-3 py-2 text-sm bg-gray-950 border border-gray-700 rounded focus:outline-none focus:border-indigo-500 font-mono"
                />
                <button type="submit" data-testid="add-ticker-btn" className="px-4 py-2 text-sm bg-indigo-700 hover:bg-indigo-600 rounded flex items-center gap-1.5">
                  <Plus className="w-4 h-4" /> Add
                </button>
              </form>
            </section>

            {/* Custom alerts */}
            <section className="rounded-lg border border-gray-800 bg-gray-900/60 p-4">
              <h2 className="text-lg font-semibold text-white mb-2">Custom alerts</h2>
              <p className="text-xs text-gray-500 mb-3">Price crosses, % moves, VIX spikes. One-hour cooldown per alert so you don't get spammed.</p>

              {alerts.length > 0 && (
                <div className="space-y-1.5 mb-4">
                  {alerts.map((a) => (
                    <div key={a.alert_id} data-testid={`custom-alert-${a.alert_id}`} className="flex items-center justify-between px-3 py-2 bg-gray-950/60 border border-gray-800 rounded">
                      <div className="text-sm">
                        <span className="font-mono text-cyan-300">{a.ticker}</span>
                        <span className="text-gray-400 mx-2">·</span>
                        <span className="text-gray-300">{alertTypeLabel(a.alert_type)} </span>
                        <span className="font-mono text-amber-300">{a.threshold}</span>
                        {a.last_fired_at && <span className="text-[10px] text-gray-500 ml-2">(last fired {new Date(a.last_fired_at).toLocaleString()})</span>}
                      </div>
                      <button onClick={() => deleteCustomAlert(a.alert_id)} className="text-red-400 hover:text-red-300" title="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <form className="grid grid-cols-1 sm:grid-cols-4 gap-2" onSubmit={createCustomAlert}>
                <input
                  value={form.ticker}
                  onChange={(e) => setForm({ ...form, ticker: e.target.value.toUpperCase() })}
                  placeholder="Ticker"
                  data-testid="custom-alert-ticker"
                  className="px-3 py-2 text-sm bg-gray-950 border border-gray-700 rounded focus:border-indigo-500 font-mono"
                />
                <select
                  value={form.alert_type}
                  onChange={(e) => setForm({ ...form, alert_type: e.target.value })}
                  data-testid="custom-alert-type"
                  className="px-3 py-2 text-sm bg-gray-950 border border-gray-700 rounded focus:border-indigo-500"
                >
                  <option value="price_above">Price above</option>
                  <option value="price_below">Price below</option>
                  <option value="pct_move_above">% change above</option>
                  <option value="pct_move_below">% change below</option>
                  <option value="vix_above">VIX above</option>
                </select>
                <input
                  value={form.threshold}
                  onChange={(e) => setForm({ ...form, threshold: e.target.value })}
                  placeholder="Threshold"
                  type="number"
                  step="any"
                  data-testid="custom-alert-threshold"
                  className="px-3 py-2 text-sm bg-gray-950 border border-gray-700 rounded focus:border-indigo-500 font-mono"
                />
                <button type="submit" data-testid="create-custom-alert-btn" className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-500 text-black font-semibold rounded flex items-center justify-center gap-1.5">
                  <Plus className="w-4 h-4" /> Add alert
                </button>
              </form>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
